import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { journeyPublicationApi } from '../../api/journeyPublicationApi';
import { lifecycleProductLabel } from '../journey-workspace/journeyLifecycleModel';
import {
  NO_PUBLICATION,
  PUBLICATION_VISIBILITIES,
  UNPUBLISH_CONFIRM_COPY,
  emptyPublication,
  findDaysMissingValidPlace,
  guidedPublicationPresenceCopy,
  isGuidedPublicationSettingsMode,
  isPublicationAbsentError,
  normalizeJourneyPublication,
  orchestratePublishWithVisibility,
  ownerPublicationActions,
  publicationErrorKind,
  publicationErrorMessage,
  publicationVisibilityCopy,
  publicationVisibilityHelpText,
  publicationVisibilityProductLabel,
  publicJourneyPath,
} from './journeyPublicationModel';
import './journey-publication.css';

function countActivities(schedule) {
  return (schedule?.days || []).reduce((sum, day) => sum + (day.activities?.length || 0), 0)
    + (schedule?.unscheduledActivities?.length || 0);
}

export default function JourneyPublicationGuidedFlow({
  open,
  onClose,
  journeyId,
  journey,
  schedule,
  disabled = false,
  onPublicationChange,
}) {
  const generation = useRef(0);
  const disposed = useRef(false);
  const busyRef = useRef(false);
  const titleId = useId();
  const confirmTitleId = useId();

  const [publication, setPublication] = useState(() => emptyPublication(journeyId));
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [successNotice, setSuccessNotice] = useState('');
  const [selectedVisibility, setSelectedVisibility] = useState('PUBLIC');
  /** 與伺服器 allowCopy 對齊；預設 OFF，不在分享時偷偷開啟。 */
  const [allowCopyDraft, setAllowCopyDraft] = useState(false);
  /** 與伺服器 allowPromotion 對齊；預設 OFF，不自動建立推廣。 */
  const [allowPromotionDraft, setAllowPromotionDraft] = useState(false);
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);

  const load = useCallback(async () => {
    const requestId = ++generation.current;
    setPhase('loading');
    setError('');
    setSuccessNotice('');
    try {
      const payload = await journeyPublicationApi.getByJourney(journeyId);
      if (requestId !== generation.current) return;
      const next = normalizeJourneyPublication(payload, journeyId);
      setPublication(next);
      setSelectedVisibility(next.visibility || 'PUBLIC');
      setAllowCopyDraft(Boolean(next.allowCopy));
      setAllowPromotionDraft(Boolean(next.allowPromotion));
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      if (isPublicationAbsentError(cause)) {
        const next = emptyPublication(journeyId);
        setPublication(next);
        setSelectedVisibility('PUBLIC');
        setAllowCopyDraft(false);
        setAllowPromotionDraft(false);
        setPhase('ready');
        return;
      }
      setError(publicationErrorMessage(publicationErrorKind(cause), cause));
      setPhase('error');
    }
  }, [journeyId]);

  useEffect(() => {
    if (!open) return undefined;
    disposed.current = false;
    load();
    return () => {
      disposed.current = true;
      generation.current += 1;
    };
  }, [open, load]);

  const actions = ownerPublicationActions(publication, {
    requesterRole: journey?.requesterRole,
    lifecycleStatus: journey?.lifecycleStatus,
  });
  const settingsMode = isGuidedPublicationSettingsMode(publication);
  const panelTitle = settingsMode ? '分享設定' : '分享行程';
  const publicPath = publicJourneyPath(publication);
  const activityCount = countActivities(schedule);
  const daysMissingPlace = findDaysMissingValidPlace(schedule);
  // 已發布僅改可見對象時不重跑地點 gate；真正 publish／republish 才擋
  const blockedByMissingPlace = daysMissingPlace.length > 0
    && !(settingsMode && publication.status === 'PUBLISHED');

  async function applyServerPublication(payload) {
    const next = normalizeJourneyPublication(payload, journeyId);
    setPublication(next);
    setSelectedVisibility(next.visibility || selectedVisibility);
    setAllowCopyDraft(Boolean(next.allowCopy));
    setAllowPromotionDraft(Boolean(next.allowPromotion));
    setPhase('ready');
    onPublicationChange?.(next);
  }

  function promotionPermissionErrorMessage(kind) {
    if (kind === 'forbidden') return '你沒有權限修改此行程的推廣設定';
    if (kind === 'unauthorized') return '請重新登入後再試。';
    if (kind === 'conflict') return '公開設定剛剛有新的變更，已重新載入最新狀態。';
    return '無法更新推廣設定，請稍後再試';
  }

  async function submitCopyPermission(nextAllowCopy) {
    if (busyRef.current || disabled || !actions.mutable) return;
    if (publication.presence === NO_PUBLICATION || !publication.publicationId) return;
    if (Boolean(publication.allowCopy) === Boolean(nextAllowCopy)) {
      setAllowCopyDraft(Boolean(nextAllowCopy));
      return;
    }
    const requestId = generation.current;
    busyRef.current = true;
    setPending(true);
    setError('');
    setSuccessNotice('');
    setAllowCopyDraft(Boolean(nextAllowCopy));
    try {
      const payload = await journeyPublicationApi.changeCopyPermission(journeyId, {
        allowCopy: Boolean(nextAllowCopy),
        expectedRowVersion: publication.rowVersion,
        confirm: true,
      });
      if (requestId !== generation.current) return;
      await applyServerPublication(payload);
      setSuccessNotice(nextAllowCopy ? '已允許其他旅人複製此行程。' : '已關閉行程複製。');
    } catch (cause) {
      if (requestId !== generation.current) return;
      setAllowCopyDraft(Boolean(publication.allowCopy));
      const kind = publicationErrorKind(cause);
      setError(publicationErrorMessage(kind, cause));
      await reloadAfterFailure(kind);
    } finally {
      busyRef.current = false;
      if (!disposed.current) setPending(false);
    }
  }

  async function submitPromotionPermission(nextAllowPromotion) {
    if (busyRef.current || disabled || !actions.mutable) return;
    if (publication.presence === NO_PUBLICATION || !publication.publicationId) return;
    if (Boolean(publication.allowPromotion) === Boolean(nextAllowPromotion)) {
      setAllowPromotionDraft(Boolean(nextAllowPromotion));
      return;
    }
    const requestId = generation.current;
    busyRef.current = true;
    setPending(true);
    setError('');
    setSuccessNotice('');
    setAllowPromotionDraft(Boolean(nextAllowPromotion));
    try {
      const payload = await journeyPublicationApi.changePromotionPermission(journeyId, {
        allowPromotion: Boolean(nextAllowPromotion),
        expectedRowVersion: publication.rowVersion,
        confirm: true,
      });
      if (requestId !== generation.current) return;
      await applyServerPublication(payload);
      setSuccessNotice(nextAllowPromotion
        ? '已允許此行程用於推廣'
        : '已停止允許此行程用於推廣');
    } catch (cause) {
      if (requestId !== generation.current) return;
      setAllowPromotionDraft(Boolean(publication.allowPromotion));
      const kind = publicationErrorKind(cause);
      setError(promotionPermissionErrorMessage(kind));
      await reloadAfterFailure(kind);
    } finally {
      busyRef.current = false;
      if (!disposed.current) setPending(false);
    }
  }

  async function reloadAfterFailure(kind) {
    try {
      const payload = await journeyPublicationApi.getByJourney(journeyId);
      if (disposed.current) return;
      await applyServerPublication(payload);
    } catch (reloadCause) {
      if (disposed.current) return;
      if (isPublicationAbsentError(reloadCause)) {
        await applyServerPublication(emptyPublication(journeyId));
        return;
      }
      if (!error) setError(publicationErrorMessage(kind, reloadCause));
    }
  }

  async function submitShare() {
    if (busyRef.current || disabled || !actions.mutable) return;
    if (blockedByMissingPlace) {
      setError('每一天都需要至少一個有效地點才能分享。請先補上缺少地點的日子。');
      return;
    }
    const requestId = generation.current;
    busyRef.current = true;
    setPending(true);
    setError('');
    setSuccessNotice('');
    try {
      if (settingsMode && publication.status === 'PUBLISHED') {
        if (publication.visibility === selectedVisibility) {
          setSuccessNotice('分享設定已是最新狀態。');
          return;
        }
        const payload = await journeyPublicationApi.changeVisibility(journeyId, {
          visibility: selectedVisibility,
          expectedRowVersion: publication.rowVersion,
          confirm: true,
        });
        if (requestId !== generation.current) return;
        await applyServerPublication(payload);
        setSuccessNotice('分享對象已更新。');
        return;
      }

      const result = await orchestratePublishWithVisibility({
        journeyId,
        current: publication,
        visibility: selectedVisibility,
        createDraft: journeyPublicationApi.createDraft,
        changeVisibility: journeyPublicationApi.changeVisibility,
        publish: journeyPublicationApi.publish,
        getByJourney: journeyPublicationApi.getByJourney,
        shouldAbort: () => requestId !== generation.current || disposed.current,
      });
      if (requestId !== generation.current) return;
      if (!result.aborted) {
        await applyServerPublication(result.publication);
        setSuccessNotice('行程已分享。');
      }
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = publicationErrorKind(cause);
      setError(publicationErrorMessage(kind, cause));
      if (kind === 'conflict' || kind === 'already-exists' || kind === 'invalid-lifecycle'
        || kind === 'lifecycle-not-writable' || kind === 'ownership-drift') {
        await reloadAfterFailure(kind);
      } else if (kind !== 'forbidden' && kind !== 'unauthorized') {
        await reloadAfterFailure(kind);
      }
    } finally {
      busyRef.current = false;
      if (!disposed.current) setPending(false);
    }
  }

  async function submitUnpublish() {
    if (busyRef.current || disabled || !actions.secondary) return;
    const requestId = generation.current;
    busyRef.current = true;
    setPending(true);
    setError('');
    setSuccessNotice('');
    try {
      const payload = await journeyPublicationApi.unpublish(journeyId, {
        expectedRowVersion: publication.rowVersion,
        confirm: true,
      });
      if (requestId !== generation.current) return;
      setConfirmUnpublish(false);
      await applyServerPublication(payload);
      setSuccessNotice('已停止分享。');
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = publicationErrorKind(cause);
      setConfirmUnpublish(false);
      setError(publicationErrorMessage(kind, cause));
      await reloadAfterFailure(kind);
    } finally {
      busyRef.current = false;
      if (!disposed.current) setPending(false);
    }
  }

  function handleClose() {
    if (busyRef.current) return;
    setConfirmUnpublish(false);
    onClose?.();
  }

  if (!open) return null;

  const primaryLabel = settingsMode && publication.status === 'PUBLISHED'
    ? '更新分享對象'
    : '分享';

  return (
    <div className="journey-publication-guided" role="presentation">
      <button
        type="button"
        className="journey-publication-guided__backdrop"
        aria-label="關閉分享設定"
        disabled={pending}
        onClick={handleClose}
      />
      <div
        className="journey-publication-guided__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="planning-publication-guided"
      >
        <header className="journey-publication-guided__head">
          <h2 id={titleId}>{panelTitle}</h2>
          <button type="button" className="journey-publication-guided__close" disabled={pending} onClick={handleClose}>
            關閉
          </button>
        </header>

        {phase === 'loading' ? (
          <p className="journey-publication__pending" aria-live="polite">載入分享設定…</p>
        ) : null}

        {phase === 'ready' || phase === 'error' ? (
          <>
            <section className="journey-publication-guided__section" aria-labelledby="guided-step-confirm">
              <h3 id="guided-step-confirm">1. 確認行程</h3>
              <p className="journey-publication-guided__journey-title">{journey?.title}</p>
              <dl className="journey-publication-guided__facts">
                {journey?.destination ? <div><dt>目的地</dt><dd>{journey.destination}</dd></div> : null}
                {journey?.dateRange ? <div><dt>日期</dt><dd>{journey.dateRange}</dd></div> : null}
                <div><dt>狀態</dt><dd>{lifecycleProductLabel(journey?.lifecycleStatus)}</dd></div>
                <div><dt>安排</dt><dd>{activityCount > 0 ? `${activityCount} 項` : '尚無安排'}</dd></div>
              </dl>
            </section>

            <section className="journey-publication-guided__section" aria-labelledby="guided-step-audience">
              <h3 id="guided-step-audience">2. 選擇分享對象</h3>
              <fieldset className="journey-publication-guided__visibility" disabled={pending || disabled || !actions.mutable}>
                <legend className="visually-hidden">分享對象</legend>
                {PUBLICATION_VISIBILITIES.map((visibility) => (
                  <label key={visibility} className="journey-publication-guided__visibility-option">
                    <input
                      type="radio"
                      name="publication-visibility"
                      value={visibility}
                      checked={selectedVisibility === visibility}
                      onChange={() => setSelectedVisibility(visibility)}
                    />
                    <span className="journey-publication-guided__visibility-label">
                      {publicationVisibilityProductLabel(visibility)}
                    </span>
                    <span className="journey-publication-guided__visibility-help">
                      {publicationVisibilityHelpText(visibility)}
                    </span>
                  </label>
                ))}
              </fieldset>
              {publication.presence !== NO_PUBLICATION && publication.publicationId ? (
                <div className="journey-publication-guided__copy-permission journey-publication__toggle-row">
                  <label
                    className="journey-publication-guided__copy-permission-label"
                    htmlFor="guided-allow-copy"
                  >
                    <input
                      id="guided-allow-copy"
                      type="checkbox"
                      data-testid="guided-allow-copy"
                      checked={allowCopyDraft}
                      disabled={pending || disabled || !actions.mutable}
                      onChange={(event) => submitCopyPermission(event.target.checked)}
                    />
                    <span>
                      <strong>允許其他旅人複製此行程</strong>
                      <small>預設關閉；開啟後，符合條件的旅人可將此行程複製為自己的行程。</small>
                    </span>
                  </label>
                </div>
              ) : null}
              {actions.owner
                && publication.presence !== NO_PUBLICATION
                && publication.publicationId
                && publication.status !== 'REMOVED' ? (
                <div className="journey-publication-guided__copy-permission journey-publication__toggle-row">
                  <label
                    className="journey-publication-guided__copy-permission-label"
                    htmlFor="guided-allow-promotion"
                  >
                    <input
                      id="guided-allow-promotion"
                      type="checkbox"
                      role="switch"
                      data-testid="guided-allow-promotion"
                      aria-checked={allowPromotionDraft}
                      checked={allowPromotionDraft}
                      disabled={pending || disabled || !actions.mutable}
                      onChange={(event) => submitPromotionPermission(event.target.checked)}
                    />
                    <span>
                      <strong>允許推廣</strong>
                      <small>
                        開啟後，你的公開行程可用於建立推廣申請。所有推廣仍須經平台審核。
                      </small>
                      <small data-testid="guided-allow-promotion-status" aria-live="polite">
                        {allowPromotionDraft ? '目前允許推廣' : '目前不允許推廣'}
                      </small>
                    </span>
                  </label>
                </div>
              ) : null}
            </section>

            <section className="journey-publication-guided__section" aria-labelledby="guided-step-share">
              <h3 id="guided-step-share">3. 分享</h3>
              <p className="journey-publication-guided__status" aria-live="polite">
                {guidedPublicationPresenceCopy(publication)}
                {' · '}
                {publicationVisibilityCopy(publication)}
              </p>
              {actions.cancelledHint ? (
                <p className="journey-publication__hint">{actions.cancelledHint}</p>
              ) : null}
              {publication.status === 'PUBLISHED' ? (
                <p className="journey-publication__hint">
                  規劃變更不會自動出現在公開頁面。若要讓其他人看到最新內容，請到公開設定更新公開版本。
                </p>
              ) : null}
              {blockedByMissingPlace ? (
                <p className="journey-publication__hint" role="status" data-testid="guided-missing-place">
                  尚有
                  {' '}
                  {daysMissingPlace.length}
                  {' '}
                  天缺少有效地點，請先到規劃補上再分享。
                </p>
              ) : null}
              {successNotice ? (
                <p className="journey-publication-guided__success" role="status">{successNotice}</p>
              ) : null}
              {error ? <p className="journey-publication__error" role="alert">{error}</p> : null}
              {pending ? (
                <p className="journey-publication__pending" aria-live="polite">更新分享設定…</p>
              ) : null}

              <div className="journey-publication__actions">
                {publicPath ? (
                  <Link className="journey-publication__link" to={publicPath} onClick={handleClose}>
                    查看分享頁面
                  </Link>
                ) : null}
                {actions.primary || (settingsMode && publication.status === 'PUBLISHED') ? (
                  <button
                    type="button"
                    className="journey-publication__primary"
                    data-testid="guided-publication-submit"
                    disabled={pending || disabled || !actions.mutable || blockedByMissingPlace}
                    onClick={submitShare}
                  >
                    {primaryLabel}
                  </button>
                ) : null}
              </div>
              {actions.secondary ? (
                <div className="journey-publication__secondary">
                  <button
                    type="button"
                    className="journey-publication__danger"
                    data-testid="guided-publication-unpublish"
                    disabled={pending || disabled}
                    onClick={() => setConfirmUnpublish(true)}
                  >
                    停止分享
                  </button>
                </div>
              ) : null}
            </section>
          </>
        ) : null}

        {phase === 'error' ? (
          <div className="journey-publication__actions">
            <button type="button" data-testid="guided-publication-reload" onClick={() => load()}>重新載入</button>
          </div>
        ) : null}

        {confirmUnpublish ? (
          <div className="journey-publication-confirm" role="presentation">
            <button
              type="button"
              className="journey-publication-confirm__backdrop"
              aria-label="關閉確認"
              disabled={pending}
              onClick={() => setConfirmUnpublish(false)}
            />
            <div
              className="journey-publication-confirm__dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby={confirmTitleId}
            >
              <h3 id={confirmTitleId}>{UNPUBLISH_CONFIRM_COPY.title}</h3>
              <p>{UNPUBLISH_CONFIRM_COPY.body}</p>
              <div className="journey-publication-confirm__actions">
                <button type="button" disabled={pending} onClick={() => setConfirmUnpublish(false)}>返回</button>
                <button
                  type="button"
                  className="journey-publication__danger"
                  disabled={pending}
                  onClick={submitUnpublish}
                >
                  {pending ? '更新中…' : '確認停止分享'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
